import React, { useMemo } from 'react'

import { CheckCircle } from '@mui/icons-material'
import { Collapse, Grid, Typography } from '@mui/material'

import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import languageService from '../../../../services/LanguageService'
import { MotivationType } from '../../../market-explorer/types/MotivationType'
import { MotivationIcon } from '../../utils/svgs'
import './MotivationTypeBadge.scss'

interface Props {
  type: MotivationType
  selected?: boolean
  active?: boolean
  onSelect?: (motivationType: MotivationType) => void
  alignLeft?: boolean
  description?: string
  disabled?: boolean
  disabledTooltip?: string
}

export const MotivationTypeBadge: React.FC<Props> = ({
  type,
  selected,
  active = false,
  alignLeft,
  description,
  onSelect,
  disabled = false,
  disabledTooltip,
}) => {
  const motivationTypeName = languageService.getTranslation('motivations', type)
  const motivationTypeDescription = description ? description : languageService.getTranslation('motivations', `${type}_description`)

  const componentClassNames = useMemo(() => {
    let classNames = 'MotivationTypeBadge'

    if (onSelect && !disabled) {
      classNames = `${classNames} MotivationTypeBadge--Selectable`
    }

    if (alignLeft) {
      classNames = `${classNames} MotivationTypeBadge--AlignLeft`
    }

    if (active) {
      classNames = `${classNames} MotivationTypeBadge--Active`
    }

    if (disabled) {
      classNames = `${classNames} MotivationTypeBadge--Disabled`
    }

    return classNames
  }, [active, alignLeft, disabled, onSelect])

  const tooltipContent =
    disabled && disabledTooltip ? (
      <Typography>{disabledTooltip}</Typography>
    ) : (
      <>
        <Typography variant="h3">
          <Grid container spacing={1.5} alignItems={'center'}>
            <Grid item>
              <MotivationIcon motivation={type} />
            </Grid>
            <Grid item xs>
              {motivationTypeName}
            </Grid>
          </Grid>
        </Typography>
        <p style={{ marginBottom: '0px' }} dangerouslySetInnerHTML={{ __html: motivationTypeDescription }}></p>
      </>
    )

  return (
    <div className={componentClassNames}>
      <GRTooltip content={tooltipContent}>
        <div
          className={selected ? 'MotivationTypeBadge__Content MotivationTypeBadge__Content--Selected' : 'MotivationTypeBadge__Content'}
          onClick={() => !disabled && onSelect && onSelect(type)}
        >
          <Grid container alignItems={'center'}>
            <Grid item>
              <div className="MotivationTypeBadge__Type">
                <MotivationIcon motivation={type} />
              </div>
            </Grid>

            <Grid item xs>
              {motivationTypeName}
            </Grid>

            <Collapse in={active} orientation="horizontal" unmountOnExit easing={{ enter: 'linear', exit: 'linear' }} timeout={100}>
              <Grid item>
                <div className="MotivationTypeBadge__Active">{active && <CheckCircle />}</div>
              </Grid>
            </Collapse>
          </Grid>
        </div>
      </GRTooltip>
    </div>
  )
}
